/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import './PaymentProjectRow.css';

// Dynamic Y-axis max with 25% buffer
const getYAxisMax = (maxVal) => {
  if (maxVal <= 0) return 100000;
  const buffered = maxVal * 1.25;
  if (buffered <= 1000) return Math.ceil(buffered / 200) * 200;
  if (buffered <= 10000) return Math.ceil(buffered / 2000) * 2000;
  if (buffered <= 100000) return Math.ceil(buffered / 25000) * 25000;
  if (buffered <= 1000000) return Math.ceil(buffered / 200000) * 200000;
  return Math.ceil(buffered / 2000000) * 2000000;
};

const formatYValue = (val) => {
  if (val === 0) return '0';
  if (val >= 10000000) return (val / 10000000).toFixed(1).replace(/\.0$/, '') + 'Cr';
  if (val >= 100000) return (val / 100000).toFixed(1).replace(/\.0$/, '') + 'L';
  if (val >= 1000) return (val / 1000).toFixed(0) + 'k';
  return val.toString();
};

const formatCurrency = (val) => {
  return "₹" + Number(val).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

// Custom Tooltip for Payment type based overview
const CustomPaymentTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="payment-tooltip-card">
        <div className="payment-tooltip-body">
          <div className="payment-tooltip-row">
            <div className="d-flex align-items-center">
              <span className="payment-tooltip-dot payments"></span>
              <span>Payments</span>
            </div>
            <span className="payment-tooltip-val">{data.payments}</span>
          </div>
          <div className="payment-tooltip-row">
            <div className="d-flex align-items-center">
              <span className="payment-tooltip-dot amount"></span>
              <span>Amount</span>
            </div>
            <span className="payment-tooltip-val">{data.amount}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for Project based overview
const CustomProjectTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="project-tooltip-card">
        <span className="project-tooltip-card-title">{label}</span>
        <div className="project-tooltip-body">
          <div className="project-tooltip-row">
            <div className="d-flex align-items-center">
              <span className="project-tooltip-dot proposed"></span>
              <span>Proposed</span>
            </div>
            <span className="project-tooltip-val">{data.proposedFormatted}</span>
          </div>
          <div className="project-tooltip-row">
            <div className="d-flex align-items-center">
              <span className="project-tooltip-dot actual"></span>
              <span>Actual</span>
            </div>
            <span className="project-tooltip-val">{data.actualFormatted}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom dotted outline cursor for Project based overview
const CustomCursor = (props) => {
  const { x, y, width, height } = props;
  if (x === undefined || y === undefined) return null;

  const cursorWidth = 48;
  const radius = 12;
  const cursorLeft = x + width / 2 - cursorWidth / 2;
  const baselineY = y + height;
  const topY = y;

  const pathData = `
    M ${cursorLeft} ${baselineY}
    L ${cursorLeft} ${topY + radius}
    A ${radius} ${radius} 0 0 1 ${cursorLeft + radius} ${topY}
    L ${cursorLeft + cursorWidth - radius} ${topY}
    A ${radius} ${radius} 0 0 1 ${cursorLeft + cursorWidth} ${topY + radius}
    L ${cursorLeft + cursorWidth} ${baselineY}
  `.replace(/\s+/g, ' ').trim();

  return (
    <g>
      <path
        d={pathData}
        fill="transparent"
        stroke="#4F46E5"
        strokeWidth={1}
        strokeDasharray="4 4"
      />
    </g>
  );
};

const CustomXAxisTickRight = (props) => {
  const { x, y, payload, activeProjectIndex, projectData } = props;
  const index = projectData.findIndex(d => d.name === payload?.value);
  const isActive = index === activeProjectIndex;
  const fullName = payload?.value || '';
  const label = fullName.length > 10 ? fullName.substring(0, 10) + '…' : fullName;

  return (
    <g>
      <title>{fullName}</title>
      <text
        x={x}
        y={y + 14}
        textAnchor="middle"
        fill={isActive ? '#4F46E5' : '#94A3B8'}
        fontSize={11}
        fontFamily="'Lexend Deca', sans-serif"
        fontWeight={isActive ? 600 : 400}
      >
        {label}
      </text>
    </g>
  );
};

const CustomYAxisTickRight = (props) => {
  const { x, y, payload } = props;
  const label = formatYValue(payload?.value ?? 0);

  return (
    <text
      x={x - 8}
      y={y}
      alignmentBaseline="middle"
      textAnchor="end"
      fill="#94A3B8"
      fontSize={10}
      fontFamily="'Lexend Deca', sans-serif"
      fontWeight={400}
    >
      {label}
    </text>
  );
};

export const PaymentProjectRow = ({
  paymentTypeDetails = [],
  projectOverviewDetails = [],
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const activeIndex = hoveredIndex;

  const [hoveredProjectIndex, setHoveredProjectIndex] = useState(null);
  const activeProjectIndex = hoveredProjectIndex;

  const safePaymentTypeDetails = Array.isArray(paymentTypeDetails) ? paymentTypeDetails : [];
  const safeProjectOverviewDetails = Array.isArray(projectOverviewDetails) ? projectOverviewDetails : [];

  // Map backend paymentTypeDetails to local state format
  const paymentData = safePaymentTypeDetails.map((item) => ({
    name: item.payment_type || '',
    payments: item.transactions ?? 0,
    amount: formatCurrency(item.amount ?? 0),
  }));

  // Map backend projectOverviewDetails to local state format
  const projectData = safeProjectOverviewDetails.map((item) => ({
    name: item.project_name || '',
    proposed: item.project_cost ?? 0,
    actual: item.spent_amount ?? 0,
    proposedFormatted: formatCurrency(item.project_cost ?? 0),
    actualFormatted: formatCurrency(item.spent_amount ?? 0),
  }));

  // Dynamic Y-axis: max value + 25% buffer
  const maxProjectValue = projectData.length > 0
    ? Math.max(...projectData.map(d => Math.max(d.proposed, d.actual)), 1)
    : 100000;
  const yAxisMax = getYAxisMax(maxProjectValue);
  const projectTickCount = 5;
  const projectTickStep = yAxisMax / projectTickCount;
  const projectYTicks = Array.from({ length: projectTickCount + 1 }, (_, i) => Math.round(i * projectTickStep));

  const maxPayments = Math.max(...paymentData.map(d => d.payments), 1);

  if (safePaymentTypeDetails.length === 0 && safeProjectOverviewDetails.length === 0) {
    return (
      <div className="payment-project-row justify-content-center align-items-center bg-white border border-[#F1F3F9] rounded-[16px]">
        <span style={{ fontFamily: "'Lexend Deca', sans-serif", fontSize: '14px', color: '#94A3B8' }}>
          No data available
        </span>
      </div>
    );
  }

  return (
    <div className="row g-4 mt-3">
      {/* Payment Type Based Card */}
      <div className="col-xl-6">
        <div className="payment-project-card payment-type">
          <div className="payment-project-header">
            <span className="payment-project-title">Payment type based overview</span>
            <div className="payment-project-legend">
              <div className="payment-project-legend-item">
                <span className="payment-project-legend-dot payments"></span>
                <span>Payments</span>
              </div>
              <div className="payment-project-legend-item">
                <span className="payment-project-legend-dot amount"></span>
                <span>Amount</span>
              </div>
            </div>
          </div>

          <div className="payment-type-body">
            <div className="payment-names-list">
              {paymentData.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <div
                    key={item.name}
                    title={item.name}
                    className={`payment-name-item ${isActive ? 'active' : ''}`}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {item.name}
                  </div>
                );
              })}
            </div>

            <div className="payment-bars-wrapper">
              <div className="payment-bars-plot-area">
                <div className="payment-bars-grid">
                  {Array.from({ length: 13 }).map((_, i) => (
                    <div key={`grid-line-${i}`} className="payment-bar-grid-line" />
                  ))}
                </div>

                {paymentData.map((item, index) => {
                  const isActive = index === activeIndex;
                  const barWidth = `${(item.payments / maxPayments) * 100}%`;
                  return (
                    <div
                      key={item.name}
                      className="payment-bar-row"
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {isActive && <div className="payment-bar-active-bg" />}
                      <div
                        className={`payment-bar-fill ${isActive ? 'active' : ''}`}
                        style={{ width: barWidth }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="payment-xaxis-labels">
                {['0', '100', '200', '300', '400', '500', '600', '700', '800', '900', '1K', '1.1K', '1.2K'].map((tick, i) => (
                  <div key={`tick-${tick}`} className="payment-xaxis-label-item">
                    {tick}
                  </div>
                ))}
              </div>

              {activeIndex !== null && paymentData[activeIndex] && hoveredIndex !== null && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${activeIndex * 56 - 72}px`,
                    left: `${(paymentData[activeIndex].payments / maxPayments) * 100}%`,
                    transform: 'translateX(-50%)',
                    pointerEvents: 'none',
                    zIndex: 10,
                    transition: 'top 0.15s ease, left 0.15s ease, transform 0.15s ease',
                  }}
                >
                  <CustomPaymentTooltip active={true} payload={[{ payload: paymentData[activeIndex] }]} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Project Based Card */}
      <div className="col-xl-6">
        <div className="payment-project-card project-based" style={{ position: 'relative' }}>
          <div className="payment-project-header">
            <span className="payment-project-title">Project based overview</span>
            <div className="payment-project-legend">
              <div className="payment-project-legend-item">
                <span className="payment-project-legend-dot proposed"></span>
                <span>Proposed</span>
              </div>
              <div className="payment-project-legend-item">
                <span className="payment-project-legend-dot actual"></span>
                <span>Actual</span>
              </div>
            </div>
          </div>

          <div style={{ height: '272px', width: '100%', flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={projectData}
                barGap={2.08}
                margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                onMouseMove={(state) => {
                  if (state && state.activeTooltipIndex !== undefined) {
                    setHoveredProjectIndex(state.activeTooltipIndex);
                  }
                }}
                onMouseLeave={() => setHoveredProjectIndex(null)}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal stroke="#F1F3F9" />
                <XAxis
                  dataKey="name"
                  height={30}
                  axisLine={false}
                  tickLine={false}
                  tick={<CustomXAxisTickRight activeProjectIndex={activeProjectIndex} projectData={projectData} />}
                />
                <YAxis
                  type="number"
                  domain={[0, yAxisMax]}
                  ticks={projectYTicks}
                  axisLine={false}
                  tickLine={false}
                  tick={<CustomYAxisTickRight />}
                />
                <Tooltip
                  content={<CustomProjectTooltip />}
                  cursor={<CustomCursor />}
                />
                <Bar
                  dataKey="proposed"
                  fill="#4F46E5"
                  barSize={10.46}
                  radius={[6, 6, 6, 6]}
                  name="Proposed"
                />
                <Bar
                  dataKey="actual"
                  fill="#DCDAFF"
                  barSize={10.46}
                  radius={[6, 6, 6, 6]}
                  name="Actual"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

